import { contentSeriesTagColors, Course } from '@/client/courses';
import { StyledNewLabel } from '@/components/courses/courses.styles';
import {
  DataTableActions,
  DataTableColumnType,
} from '@/components/tables/crud';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { DateFormats } from '@/system-settings/enums';
import { AppChip } from '@/ui/chip';
import DatatableThumbnail from '@/ui/datatable-thumbnail/DatatableThumbnail';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { formatDate } from '@/utils/date/date.helper';
import {
  CloneIcon,
  CustomizableIcon,
  formatTags,
  isNewCourse,
  isUpcomingCourse,
} from '@/utils/helpers';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';

type CoursesColumnsOptions = {
  menuItems: (course: Course) => MenuItem[];
  cloningCourseIds: string[];
};

export const useCoursesColumns = ({
  menuItems,
  cloningCourseIds,
}: CoursesColumnsOptions) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('content'),
      sortable: true,
      filterable: false,
      render: (row: Course) => {
        return (
          <FlexContainer gap={12}>
            {row?.courseThumbnail?.signedUrl && (
              <DatatableThumbnail
                className={
                  isUpcomingCourse(row?.releaseDate) ? 'upcoming-course' : ''
                }
                url={row?.courseThumbnail?.signedUrl}
              />
            )}
            <FlexContainer
              direction="column"
              align="center"
              className="align-self-center"
            >
              <span>
                {isNewCourse(row?.releaseDate) && (
                  <StyledNewLabel>{t('generic.new')}</StyledNewLabel>
                )}
                <FlexContainer gap={4}>
                  <span className="mr-2">{row?.name}</span>
                  {row?.account && (
                    <AppChip label={t('generic.custom')} type="red" size="s" />
                  )}

                  {row?.contentSeries && (
                    <AppChip
                      label={t(`content.series.${row?.contentSeries}`)}
                      type={contentSeriesTagColors[row?.contentSeries]}
                      size="s"
                    />
                  )}
                </FlexContainer>
              </span>

              {!!row?.tags?.length && (
                <span className="secondary-text">
                  <span>{t('content.tags')}: </span>
                  {formatTags(row?.tags)}
                </span>
              )}
            </FlexContainer>
          </FlexContainer>
        );
      },
    },
    {
      field: 'status',
      header: t('generic.status'),
      sortable: false,
      filterable: false,
      render: (row: Course) => {
        return isUpcomingCourse(row?.releaseDate) ? (
          <FlexContainer
            direction="column"
            justify="flex-start"
            gap={9}
            className="min-w-max"
          >
            <AppChip
              label={t('content.upcoming')}
              type="secondary"
              size="s"
              tooltip={t('content.upcoming.tooltip', {
                date: formatDate(row.releaseDate, DateFormats.DATEONLY),
              })}
            />
            {row?.isPreviewable && (
              <AppChip
                label={t('course.preview.ready')}
                type="secondary"
                size="s"
              />
            )}
          </FlexContainer>
        ) : (
          <AppChip label={t('generic.available')} type="primary" size="s" />
        );
      },
    },
  ];

  columns.push({
    field: 'cloneSettings.isCloneable',
    header: t('content.customizable'),
    sortable: false,
    filterable: false,
    render: (row: Course) => {
      const showClone = !!row?.cloneSettings?.isCloneable;
      const showAi = !!row?.aiCustomizationSettings?.isEnabled;

      if (!showClone && !showAi) return null;

      return (
        <FlexContainer gap={6} justify="flex-start">
          {showClone && (
            <>
              <span className={`clonable-icon-${row.id} pt-2 d-inline-flex`}>
                <CloneIcon />
              </span>
              <Tooltip
                position="top"
                target={`.clonable-icon-${row.id}`}
                content={t('content.clonable.tooltip')}
              />
            </>
          )}
          {showAi && (
            <>
              <span
                className={`ai-customizable-icon-${row.id} pt-2 d-inline-flex`}
              >
                <CustomizableIcon />
              </span>
              <Tooltip
                position="top"
                target={`.ai-customizable-icon-${row.id}`}
                content={t('content.aiCustomizable.tooltip')}
              />
            </>
          )}
        </FlexContainer>
      );
    },
  });

  if (!account?.isSystem) {
    columns.push({
      field: 'totalNumberOfEnrollments',
      header: t('courses.statistics.total_enrollments'),
      sortable: true,
      filterable: false,
      render: (row: Course) => (
        <span>{row.totalNumberOfEnrollments || '—'}</span>
      ),
    });

    columns.push({
      field: 'completionPercentage',
      header: t('courses.statistics.completion_percentage'),
      sortable: true,
      filterable: false,
      render: (row: Course) => (
        <span>
          {row.completionPercentage ? `${row.completionPercentage}%` : '—'}
        </span>
      ),
    });
  }

  if (account?.isSystem) {
    columns.push({
      field: 'courseLastUpdatedAt',
      header: t('generic.last.updated'),
      sortable: false,
      filterable: false,
      render: (row: Course) =>
        row?.courseLastUpdatedAt ? (
          <FormatDate date={row?.courseLastUpdatedAt} />
        ) : (
          t('generic.never')
        ),
    });

    columns.push({
      field: 'customContent',
      header: t('content.custom.content'),
      sortable: false,
      filterable: false,
      render: (row: Course) =>
        row?.account ? (
          <AppChip label={t('dialog.yes')} type="primary" size="s" />
        ) : (
          <AppChip label={t('dialog.no')} type="secondary" size="s" />
        ),
    });
  }

  columns.push({
    field: 'actions',
    header: t('generic.actions'),
    sortable: false,
    filterable: false,
    style: {
      width: '80px',
      textAlign: 'center',
    },
    render: (row: Course) =>
      cloningCourseIds.includes(row.id) ? (
        <FlexContainer>
          <ProgressSpinner style={{ width: '24px', height: '24px' }} />
        </FlexContainer>
      ) : (
        <DataTableActions
          disabled={menuItems(row).length < 1}
          menuItems={menuItems(row)}
        />
      ),
  });

  return columns;
};
